import { useEffect } from 'react';
import { SYS_SPACE_FIXED_200 } from '@oneaudi/unified-web-alpha-common';
const isScrollPositionEnd = (container) => {
    if (!container)
        return false;
    return container.scrollLeft + container.offsetWidth >= container.scrollWidth;
};
const scrollBySlide = (direction, container, slideWidth) => {
    if (!container || !slideWidth)
        return;
    const root = document.documentElement;
    const pageMargin = parseInt(getComputedStyle(root).getPropertyValue('--page-margin'));
    const marginRight = parseInt(SYS_SPACE_FIXED_200);
    let distance = slideWidth;
    if (direction === 'next') {
        distance =
            slideWidth + (container.scrollLeft === 0 ? pageMargin : marginRight);
        container.scrollBy({ left: distance, behavior: 'smooth' });
        return;
    }
    distance =
        slideWidth + (isScrollPositionEnd(container) ? pageMargin : marginRight);
    container.scrollBy({ left: -distance, behavior: 'smooth' });
};
// Prevents hijacking of text input caret via arrow keys when a user is typing
const isTextInputLike = (el) => {
    if (!el || !(el instanceof HTMLElement))
        return false;
    const tag = el.tagName.toLowerCase();
    if (tag === 'input') {
        const type = el.type;
        return [
            'text',
            'search',
            'email',
            'url',
            'tel',
            'password',
            'number',
            'date',
            'time',
            'datetime-local',
            'month',
            'week'
        ].includes(type);
    }
    if (tag === 'textarea')
        return true;
    if (el.isContentEditable)
        return true;
    return false;
};
export const useCarouselKeyboardNavigation = ({ containerRef, slideWidth, onMove }) => {
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const getSlides = () => {
            const nodeList = container.querySelectorAll('li');
            return Array.from(nodeList).filter((li) => li.parentElement === container);
        };
        const handler = (e) => {
            if (e.defaultPrevented)
                return;
            if (!containerRef.current)
                return;
            const key = e.key;
            if (key === 'Tab') {
                // Keep Tab out of the carousel: move focus to the next/prev outside element
                const doc = container.ownerDocument || document;
                const allFocusable = Array.from(doc.querySelectorAll([
                    'a[href]',
                    'button:not([disabled])',
                    'input:not([disabled]):not([type="hidden"])',
                    'select:not([disabled])',
                    'textarea:not([disabled])',
                    '[tabindex]:not([tabindex="-1"])',
                    '[contenteditable="true"]'
                ].join(','))).filter((el) => el.tabIndex !== -1);
                const outside = allFocusable.filter((el) => !container.contains(el));
                const before = outside.filter((el) => (container.compareDocumentPosition(el) &
                    Node.DOCUMENT_POSITION_PRECEDING) >
                    0);
                const after = outside.filter((el) => (container.compareDocumentPosition(el) &
                    Node.DOCUMENT_POSITION_FOLLOWING) >
                    0);
                const target = e.shiftKey
                    ? before.length > 0
                        ? before[before.length - 1]
                        : undefined
                    : after.length > 0
                        ? after[0]
                        : undefined;
                if (target) {
                    e.preventDefault();
                    target.focus();
                    return;
                }
                // If no outside target, allow default behavior
                return;
            }
            if (key !== 'ArrowRight' && key !== 'ArrowLeft')
                return;
            if (isTextInputLike(e.target))
                return;
            e.preventDefault();
            const slides = getSlides();
            if (!slides.length)
                return;
            const direction = key === 'ArrowRight' ? 'next' : 'prev';
            const active = document.activeElement?.closest('li');
            let baseIndex = active ? slides.indexOf(active) : -1;
            if (baseIndex === -1) {
                const visible = slides.filter((li) => li.getAttribute('aria-hidden') === 'false');
                if (visible.length) {
                    const last = visible[visible.length - 1];
                    const first = visible[0];
                    baseIndex =
                        direction === 'next' ? slides.indexOf(last) : slides.indexOf(first);
                }
                else {
                    baseIndex = 0;
                }
            }
            const delta = direction === 'next' ? 1 : -1;
            let nextIndex = baseIndex + delta;
            nextIndex = Math.max(0, Math.min(slides.length - 1, nextIndex));
            if (nextIndex === baseIndex)
                return; // Nothing to do
            scrollBySlide(direction, containerRef.current, slideWidth);
            const nextSlide = slides[nextIndex];
            if (nextSlide && typeof nextSlide.focus === 'function') {
                nextSlide.focus({ preventScroll: true });
            }
            if (typeof onMove === 'function') {
                onMove(nextIndex);
            }
        };
        container.addEventListener('keydown', handler, { passive: false });
        return () => container.removeEventListener('keydown', handler);
    }, [containerRef, slideWidth, onMove]);
};
export default useCarouselKeyboardNavigation;
//# sourceMappingURL=useCarouselKeyboardNavigation.js.map